import type { RacingEvent } from "@/hooks/firestore/models/racingEvent";
import type { Always, NullableMaybe } from "@/types/utilities";
import type {
  ProcessedCompetitor,
  ProcessedCompetitorMarket,
} from "@/features/race/hooks/useSortedCompetitors";
import type { RacingMarketType } from "@/hooks/firestore/models/market";
import {
  BettingTabs,
  type BettingTabs as BettingTabsType,
} from "@/types/BetTypes";
import type { ProcessedMarkets } from "@/hooks/firestore/betting/useEvent";
import { isSRMTab } from "@/hooks/useMultiSlug";
import escapeRegExp from "lodash/escapeRegExp";

const getJockeyNameWithLabel = (
  jockeyName: NullableMaybe<string>,
  sport: RacingEvent["sport"],
) => {
  if (sport === "GREYHOUNDS" || !jockeyName) return;

  const prefix = sport === "HARNESS_RACING" ? "D" : "J";

  return `${prefix}: ${jockeyName}`;
};

const spTypeMap: Partial<Record<RacingMarketType, RacingMarketType>> = {
  RACING_WINNER: "RACING_WINNER_SP",
  RACING_TOP_2: "RACING_TOP_2_SP",
  RACING_TOP_3: "RACING_TOP_3_SP",
  RACING_PLACE: "RACING_PLACE_SP",
  RACING_TOP_4: "RACING_TOP_4_SP",
};

const RACING_COMPLETED_STATUSES = ["CANCELLED", "FINAL", "DONE", "ABANDONED"];

const getCompetitorOutcome = (
  competitorId: string,
  marketType: RacingMarketType,
  markets: ProcessedCompetitorMarket[],
  forceSp: boolean = false,
) => {
  // get market type
  const market = markets.find((market) => market.marketType === marketType);

  const marketSp = markets.find(
    (market) => market.marketType === spTypeMap[marketType],
  );

  const actualMarket = forceSp ? marketSp : market ?? marketSp;

  if (!actualMarket) return;

  const outcomeForCompetitor = Object.entries(actualMarket.outcomes).find(
    ([_, outcome]) => outcome.active && outcome.competitorId === competitorId,
  );

  if (!outcomeForCompetitor) return;

  return {
    ...outcomeForCompetitor[1],
    id: outcomeForCompetitor[0],
    marketStatus: actualMarket.status,
    marketId: actualMarket.id,
    actualMarketType: actualMarket.marketType,
  };
};

const urlToBetTab = (tab: string): BettingTabsType => {
  if (tab === "top4" || tab === "win-place") {
    return BettingTabs.RACING_SINGLES;
  }

  if (tab === "quaddies") {
    return BettingTabs.RACING_QUADDIES;
  }

  if (tab === "exotics") {
    return BettingTabs.RACING_EXOTICS;
  }

  if (isSRMTab(tab)) {
    return BettingTabs.RACING_SEM;
  }

  return BettingTabs.RACING_SINGLES;
};

const speedMapPositionMap: Record<number, string> = {
  1: "Leader",
  2: "Pace",
  3: "Off-Pace",
  4: "Midfield",
  5: "Off-Midfield",
  6: "Backmarker",
};

// Splits the comment into text and the competitors it tips, in order
const parseRaceComment = (
  comment: string,
  competitors: ProcessedCompetitor[],
) => {
  if (!competitors.length) {
    return [comment];
  }

  // Finds each runner's name followed by its number, like "AETHELWULF (12)".
  // Ignores case, and any other bracketed number like "heavy (8)"
  const competitorNamesPattern = new RegExp(
    `(${competitors
      .map(
        ({ name, number }) =>
          `${escapeRegExp(name).replace(/ /g, "\\s+")}\\s*\\(${number}\\)`,
      )
      .join("|")})`,
    "i",
  );

  // split keeps each matched mention, so parts go text, mention, text, mention...
  return comment.split(competitorNamesPattern).flatMap((part, index) => {
    const isMention = index % 2 === 1;
    if (isMention) {
      return (
        competitors.find(({ number }) => part.endsWith(`(${number})`)) ?? part
      );
    }

    // Drop the empty text when a runner starts or ends the comment
    return part ? [part] : [];
  });
};

function groupBy<T, K extends keyof T>(
  array: T[],
  key: K,
): Record<string, T[]> {
  return array.reduce(
    (acc, item) => {
      const group = String(item[key]);
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );
}

function mapValues<T extends Record<string, any>, R>(
  obj: T,
  iteratee: (value: T[keyof T], key: keyof T) => R,
): Record<keyof T, R> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      iteratee(value, key as keyof T),
    ]),
  ) as Record<keyof T, R>;
}

const sameRaceMultiLabelMap: Partial<
  Record<
    NonNullable<ProcessedMarkets[number]["outcomes"][string]["type"]>,
    string
  >
> = {
  RUNNER_WIN: "Win",
  RUNNER_TOP_2: "Top 2",
  RUNNER_TOP_3: "Top 3",
  RUNNER_TOP_4: "Top 4",
};

type CompetitorOutcome = Always<ReturnType<typeof getCompetitorOutcome>>;

/**
 * Resolve `{ market, outcome }` from a race's markets array given a clicked
 * `CompetitorOutcome`. Returns null if either lookup misses. Used by
 * WinPlace / Top4 / Tips to feed the strict-typed `toggle()`.
 */
const findMarketAndOutcome = (
  markets: ProcessedMarkets,
  outcome: CompetitorOutcome,
): {
  market: ProcessedMarkets[number];
  outcomeRecord: ProcessedMarkets[number]["outcomes"][string];
} | null => {
  const market = markets.find((m) => m.id === outcome.marketId);
  const outcomeRecord = market?.outcomes?.[outcome.id];
  if (!market || !outcomeRecord) return null;
  return { market, outcomeRecord };
};

export { CompetitorOutcome };

export {
  getJockeyNameWithLabel,
  getCompetitorOutcome,
  urlToBetTab,
  speedMapPositionMap,
  parseRaceComment,
  groupBy,
  mapValues,
  sameRaceMultiLabelMap,
  findMarketAndOutcome,
  RACING_COMPLETED_STATUSES,
};
